import { Autocomplete, CircularProgress, TextField, Paper } from "@mui/material";
import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { useMultiSiteContext } from "@hooks/use-site-context";
import { fetchWithTokenRefresh } from "@hooks/shared/fetch-with-token-refresh";
import { getLocalStorageItem } from "@utils/index";
import { USER_SESSION_KEY } from "@hooks/use-auth-context/utils";
import { addQueryArgs } from "@wordpress/url";
import type { WpUserListItem } from "@hooks/use-get/user-list";
import { UserIdentifierDisplay } from "@components/user-identifier-display";

const SITE_USERS_URL = "wp-json/smassets/v1/site-users";
const SITE_USERS_SEARCH_URL = "wp-json/smassets/v1/site-users/search";

interface UserAutocompleteProps {
  value: number | string | null;
  onChange: (value: number | null) => void;
  label?: string;
  disabled?: boolean;
}

export const UserAutocomplete = ({
  value,
  onChange,
  label = "Assigned User",
  disabled,
}: UserAutocompleteProps) => {
  const [options, setOptions] = useState<WpUserListItem[]>([]);
  const [selectedOption, setSelectedOption] = useState<WpUserListItem | null>(null);
  const [loading, setLoading] = useState(false);
  // false when we have an initial value that hasn't been fetched yet
  const [initialLoaded, setInitialLoaded] = useState(!value);
  const { getApiUrl, blogId } = useMultiSiteContext();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchUsers = useCallback(
    async (search: string) => {
      setLoading(true);
      try {
        const token = getLocalStorageItem(USER_SESSION_KEY)?.authToken ?? "";
        const baseUrl = search.length >= 2
          ? getApiUrl(SITE_USERS_SEARCH_URL)
          : getApiUrl(SITE_USERS_URL);

        const params: Record<string, string | number> = {
          per_page: 50,
          ...(blogId !== null ? { blog_id: blogId } : {}),
          ...(search.length >= 2 ? { q: search, limit: 50 } : {}),
        };

        const response = await fetchWithTokenRefresh(
          addQueryArgs(baseUrl, params),
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
          null
        );

        if (!response.ok) return;

        const json = await response.json();
        const items = json.items ?? [];
        setOptions(
          items.map((u: any) => ({
            id: u.wp_user_id,
            name: [u.first_name, u.last_name].filter(Boolean).join(" ") || u.user_login,
            role: u.role_type ?? u.role ?? null,
            grade: u.grade ?? null,
          }))
        );
      } finally {
        setLoading(false);
      }
    },
    [getApiUrl, blogId]
  );

  // Fetch a single user by wp_user_id to seed the initial display value
  const fetchSingleUser = useCallback(
    async (wpUserId: number) => {
      setLoading(true);
      try {
        const token = getLocalStorageItem(USER_SESSION_KEY)?.authToken ?? "";
        const url = getApiUrl(`wp-json/smassets/v1/site-users/${wpUserId}`);
        const response = await fetchWithTokenRefresh(
          url,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
          null
        );
        if (!response.ok) return;
        const u = await response.json();
        const item: WpUserListItem = {
          id: u.wp_user_id,
          name: [u.first_name, u.last_name].filter(Boolean).join(" ") || u.user_login,
          role: u.role_type ?? u.role ?? null,
          grade: u.grade ?? null,
        };
        setOptions([item]);
        setSelectedOption(item);
        setInitialLoaded(true);
      } finally {
        setLoading(false);
      }
    },
    [getApiUrl]
  );

  // On mount only — seed the selected option if an initial value is provided
  // Seed the selected option when an initial value is provided (or changes)
  useEffect(() => {
    if (!value) return;
    setInitialLoaded(false);
    fetchSingleUser(Number(value));
  }, [value, fetchSingleUser]);

  // If the parent clears the value externally, drop the selection
  useEffect(() => {
    if (!value && selectedOption) setSelectedOption(null);
  }, [value, selectedOption]);

  // Merge selectedOption into the dropdown options so MUI can match it
  const dropdownOptions = useMemo(() => {
    if (!selectedOption) return options;
    return options.some((o) => o.id === selectedOption.id)
      ? options
      : [selectedOption, ...options];
  }, [options, selectedOption]);

  // Show a spinner while the initial user is being fetched
  if (!initialLoaded) {
    return (
      <TextField
        label={label}
        variant="outlined"
        fullWidth
        disabled
        value=""
        slotProps={{
          input: {
            endAdornment: <CircularProgress color="inherit" size={20} />,
          },
        }}
      />
    );
  }

  // Once initialLoaded is true, the Autocomplete mounts with value already set.
  // MUI initialises its internal input text from getOptionLabel(value) at mount time,
  // so the user's name displays correctly without any inputValue wrangling.
  return (
    <Autocomplete
      options={dropdownOptions}
      getOptionLabel={(option: WpUserListItem) => option.name}
      getOptionKey={(option: WpUserListItem) => option.id}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      filterOptions={(x) => x}
      loading={loading}
      value={selectedOption}
      disabled={disabled}
      noOptionsText="Start typing to search for a user"
      onChange={(_, newValue: WpUserListItem | null) => {
        setSelectedOption(newValue);
        onChange(newValue ? newValue.id : null);
      }}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <UserIdentifierDisplay
            firstName={option.name.split(" ")[0]}
            lastName={option.name.split(" ").slice(1).join(" ")}
            role={option.role}
            grade={option.grade}
          />
        </li>
      )}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === "input") {
          if (debounceRef.current) clearTimeout(debounceRef.current);
          debounceRef.current = setTimeout(() => fetchUsers(newInputValue), 300);
        } else if (reason === "clear") {
          setSelectedOption(null);
          onChange(null);
        }
      }}
      slots={{
        paper: (props) => <Paper {...props} elevation={8} />,
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          fullWidth
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
};

export default UserAutocomplete;
